import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect } from "react";

import styled from "styled-components/macro";

import { faTimesCircle } from "@fortawesome/pro-solid-svg-icons";
import { useSignups } from "Signups/state.js";
import { AnimatePresence } from "framer-motion";
import cloneDeep from "lodash/cloneDeep";

import FnrButton from "UI/FnrButton3/FnrButton";

import createRandomID from "Utilities/RandomID";

import AddRespondentButton from "./AddRespondentButton";
import RespondentEntryNonUser from "./RespondentEntryNonUser";
import RespondentEntryUser from "./RespondentEntryUser";
import SharedExtendedFields from "./SharedExtendedFields";
import {
  useAddRespondentIsEnabled,
  useReFetchSignupListInfo,
} from "./stateHooks";
import { useNumberOfSlotsFromRespondents } from "./utils/getRespondentNumbers";

export default function RespondentsList({
  signupItem,
  respondents,
  setRespondents,
  resetRespondents,
  setErrorMessage,
  handleCloseClick,
  SubmitButton,
}) {
  const [state, dispatch] = useSignups();
  const { hasUnsavedEdits } = state;
  const { actionNamePresentTense } = state.currentSignupList.signupListVerbs;
  const { extendedFields = [] } = signupItem;
  const currentRespondentSlots = useNumberOfSlotsFromRespondents(respondents);

  const [addRespondentIsEnabled, disabledMessage] = useAddRespondentIsEnabled(
    respondents,
    signupItem
  );

  const setChecked = (userID, newIsCheckedValue) => {
    const newRespondents = cloneDeep(respondents);
    newRespondents.find((r) => r.userID === userID).isChecked =
      newIsCheckedValue;

    if (addRespondentIsEnabled || !newIsCheckedValue) {
      setErrorMessage(null);
      setRespondents(newRespondents);
      dispatch({ type: "updateHasUnsavedEdits", payload: true });
    } else {
      setErrorMessage(disabledMessage);
    }
  };

  const updateName = (userID, newValue) => {
    const newRespondents = [...respondents];
    const personToChange = respondents.find((r) => r.userID === userID);
    personToChange.name = newValue;
    setRespondents(newRespondents);
  };

  const addNewRespondent = () => {
    const newRespondents = [...respondents];
    newRespondents.push({
      userID: createRandomID(8),
      isAdditionalName: true,
      isChecked: true,
      name: "",
    });
    setRespondents(newRespondents);
  };

  const reFetchSignupListInfo = useReFetchSignupListInfo();
  const addNewHouseholdRespondent = (newUser, householdID) => {
    dispatch({
      type: "Add New Household User",
      payload: { newUser, householdID },
    });
    reFetchSignupListInfo();
  };

  const deleteRespondent = (idToDelete) => {
    const newRespondents = respondents.filter((r) => r.userID !== idToDelete);
    setRespondents(newRespondents);
  };

  const handleCancelClick = () => {
    dispatch({ type: "clearExtendedFieldInputResponse" });
    dispatch({ type: "setFieldValidationErrors", payload: {} });
    resetRespondents();

    // close the expanded item
    dispatch({ type: "expandedItemID", ID: null });
  };

  const getAddRespondentUserID = () => {
    const res = respondents.find((r) => !r.isAdditionalName);
    if (!res) return null;
    return res?.userID || null;
  };

  const userFields = extendedFields.filter((f) => f.entityType === "user");
  const householdFields = extendedFields.filter(
    (f) => f.entityType === "household"
  );
  const spouseFields = extendedFields.filter((f) => f.entityType === "spouse");
  const selectedRespondents = respondents.filter(
    (r) => !r.isAdditionalName && r.isChecked
  );
  const getSharedFields = () => {
    return [...spouseFields, ...householdFields];
  };

  const viewableSharedFields = getSharedFields();

  useEffect(
    function removeAdditionalNamesWhenNoHHRespondents() {
      const hasCheckedHHRespondents = Boolean(
        respondents?.filter((r) => !r?.isAdditionalName && r?.isChecked)?.length
      );
      const hasCheckedAdditionalNames = Boolean(
        respondents?.filter((r) => r?.isAdditionalName)?.length
      );
      if (hasCheckedAdditionalNames && !hasCheckedHHRespondents) {
        const respondentsWithoutAdditionalNames = respondents?.filter(
          (r) => !r?.isAdditionalName
        );
        setRespondents(respondentsWithoutAdditionalNames);
      }
    },
    [respondents, setRespondents]
  );

  return (
    <Wrapper>
      <ListTitle>
        Please select who is {actionNamePresentTense}:
        <CloseButton onClick={handleCloseClick}>
          <FontAwesomeIcon icon={faTimesCircle} />
        </CloseButton>
      </ListTitle>

      <List data-cy="public-signup-respondent-list">
        {respondents.map((person, i) => {
          if (!person.isAdditionalName) {
            return (
              <RespondentEntryUser
                key={`${person.userID}-${i}`}
                person={person}
                setChecked={setChecked}
                signupItem={signupItem}
                userExtendedFields={userFields}
                hasAnyFields={extendedFields.length > 0}
              />
            );
          } else {
            return (
              <RespondentEntryNonUser
                key={person.userID}
                person={person}
                setChecked={setChecked}
                updateName={updateName}
                deleteRespondent={deleteRespondent}
                signupItem={signupItem}
                userExtendedFields={[]}
              />
            );
          }
        })}
        <AnimatePresence>
          {Boolean(selectedRespondents.length) &&
            viewableSharedFields.length > 0 && (
              <SharedExtendedFields
                fields={viewableSharedFields}
                signupItem={signupItem}
                key="shared_fields"
              />
            )}
          {addRespondentIsEnabled && (
            <AddRespondentButton
              disabled={!addRespondentIsEnabled}
              addNewRespondent={addNewRespondent}
              addNewHouseholdRespondent={addNewHouseholdRespondent}
              userID={getAddRespondentUserID()}
              userData={state?.user || {}}
              isAnonymousUser={state?.user?.isAnonymousUser}
              numberOfRespondents={currentRespondentSlots}
              key="add_respondent_button"
            />
          )}
        </AnimatePresence>
      </List>
      <Buttons>
        {hasUnsavedEdits && (
          <FnrButton
            color="gray"
            data-cy="public-signup-cancel-button"
            onClick={handleCancelClick}
          >
            Cancel
          </FnrButton>
        )}
        <SubmitButton />
      </Buttons>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-bottom: 10px;
  background: white;
  border-radius: 14px;
  margin: -2px -15px 15px -15px;
  padding: 3px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 8px 24px 0 #eeeeeeff, 0 4px 8px 0 #42474c0f, 0 0 1px 0 #42474c52;
`;

const ListTitle = styled.div`
  position: relative;
  background-image: linear-gradient(159deg, #2f3f4f 5%, #13426b 100%);
  border-radius: 11px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  padding: 10px;
  padding-right: 35px;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 4px;
  right: 10px;
  font-size: 24px;
  opacity: 0.9;
  cursor: pointer;
  transition: opacity 150ms;

  &:hover {
    opacity: 1;
  }

  &:active {
    transform: scale(0.97);
  }
`;

const List = styled.div`
  //max-height: 350px;
  //overflow-y: auto;
  padding: 6px 0;
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px;
`;

RespondentsList.propTypes = {
  signupItem: PropTypes.object,
  numberOfSlots: PropTypes.number,
  respondents: PropTypes.array,
  setRespondents: PropTypes.func,
  resetRespondents: PropTypes.func,
  setErrorMessage: PropTypes.func,
};
